import { useEffect, useRef, useState } from "react";
import { ExternalLink, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getShopLinks } from "@/lib/shops";
import type { Zelt } from "@/data/zelte";

interface ShopLinksProps {
  zelt: Pick<Zelt, "name" | "marke">;
  size?: "default" | "lg" | "full";
  className?: string;
}

export function ShopLinks({ zelt, size = "default", className }: ShopLinksProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const shops = getShopLinks(zelt);

  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn("relative", size === "full" && "w-full", className)}>
      <Button
        size={size === "lg" ? "lg" : "default"}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          "rounded-xl gap-2",
          size === "lg" && "w-full h-14 text-base px-6 btn-premium text-primary-foreground",
          size === "full" && "w-full h-11 rounded-xl btn-premium text-primary-foreground",
          size === "default" && "btn-premium text-primary-foreground"
        )}
      >
        <span className="relative z-10 flex items-center">
          Zum Angebot
          <ChevronDown className={cn("h-4 w-4 ml-2 transition-transform", open && "rotate-180")} />
        </span>
      </Button>

      <div
        role="menu"
        className={cn(
          "absolute z-40 mt-2 min-w-[240px] w-full",
          "bg-background border rounded-xl shadow-lg overflow-hidden",
          "origin-top transition-all duration-200",
          open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
        )}
      >
        {shops.map(shop => (
          <a
            key={shop.id}
            href={shop.href}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-muted/60 transition-colors"
            onClick={() => setOpen(false)}
          >
            <span>
              <span className="font-medium block">{shop.label}</span>
              <span className="text-xs text-muted-foreground">{shop.hint}</span>
            </span>
            <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}
